import type { FormulaSpec, LuachResult, LuachShul } from '@app/luach'

/**
 * A sample schedule for the marketing page, so the home page can show the
 * product by rendering the PRODUCT — the same `<Luach>` the office prints and
 * the public page serves — rather than a screenshot of it.
 *
 * A picture of a screen is a copy of the design that stops being true the
 * moment the design moves, and nobody notices until a visitor sees a sheet the
 * app no longer produces. This is the typographic version of the rule the whole
 * project rests on: compute a time in exactly one place. Here the rendering is
 * the thing computed once, and the marketing page is simply another caller.
 *
 * It is typed as a real `LuachResult`, which is the mechanism that keeps it
 * honest: a change to the sheet's contract fails the typecheck here instead of
 * quietly leaving a stale image on the front page.
 *
 * The congregation is INVENTED, and must stay that way — no real roster, no
 * real shul, in the repo (`15-security.md`). The times are internally
 * consistent with the shkia below so a reader who checks the arithmetic finds
 * it holds: mincha is shkia − 40, candle lighting is shkia − 18.
 */

const SHKIA = '4:46 PM'

/** Offset-from-a-zman timing, the shape the resolver emits per line. */
function offsetOf(
  anchor: string,
  minutes: number,
  direction: 'before' | 'after',
  anchorTime: string | null = null,
): FormulaSpec {
  return {
    kind: 'offset',
    fixedTime: null,
    offsetMinutes: minutes,
    direction,
    anchor,
    roundStepMinutes: 1,
    roundDir: 'nearest',
    notEarlierThan: null,
    notLaterThan: null,
    partsCount: 0,
    anchorTime,
  }
}

/** A time the shul simply sets, with no zman behind it. */
function fixedAt(hhmm: string): FormulaSpec {
  return {
    kind: 'fixed',
    fixedTime: hhmm,
    offsetMinutes: null,
    direction: null,
    anchor: null,
    roundStepMinutes: 1,
    roundDir: 'nearest',
    notEarlierThan: null,
    notLaterThan: null,
    partsCount: 0,
  }
}

/**
 * The fields every line carries and this sample never varies. Spelling them out
 * once keeps each entry below down to what it actually says.
 */
const LINE = {
  /* Filled by `withPlacementIds` below. The sample has no `occasion_line`
     behind it, so its placement identity is minted rather than carried. */
  lineId: '',
  noteText: null,
  displayText: null,
  subText: null,
  rootZman: 'shkia',
  rationale: null,
  // The sample stands for a shul whose rules are settled, so nothing on it
  // carries a caveat: every line is confirmed and none is awaiting an answer.
  provenance: 'gabbai',
  needsReview: false,
  kind: 'tefilla' as string | null,
  contains: [] as string[],
  moment: null as string | null,
  minyanIndex: null,
  room: null as { name: string } | null,
  emphasis: 'primary',
  overrideNote: null,
  follows: null,
  printDayOffset: null,
  requireFlags: [] as string[],
  excludeFlags: [] as string[],
  conditionLabel: null,
} as const

/**
 * Which date's zmanim a line resolves against. Getting this wrong shifts a time
 * by a whole day, so it is stated per day-block rather than defaulted — the
 * erev's mincha hangs off the erev's shkia, and motzei Shabbos hangs off
 * Shabbos's own.
 */
const EREV = { ...LINE, dayOffset: -1 }
const DAY = { ...LINE, dayOffset: 0 }

export const DEMO_SHUL: LuachShul = {
  name: 'Congregation Ohel Yaakov',
  nameHe: 'קהילת אהל יעקב',
  rabbiLineHe: 'הרב אליהו וינברג שליט״א',
  addressLine: '118 Maple Terrace',
  city: 'Fairview',
  region: 'NJ',
  phone: '(555) 010-4417',
}

/**
 * Stamp each line with its own placement identity.
 *
 * A row is identified by the placement it came from, and this sample has none —
 * it is written by hand rather than resolved from `occasion_line`. Each line
 * here IS its own placement and the keys are unique within the sample, so the
 * key is the honest stand-in. Stamped once rather than written on every line,
 * which would be a second thing to keep unique by hand and get wrong silently.
 */
const withPlacementIds = (r: LuachResult): LuachResult => ({
  ...r,
  days: r.days.map((d) => ({
    ...d,
    entries: d.entries.map((e) => ({ ...e, lineId: e.key })),
  })),
})

const SAMPLE: LuachResult = {
  occasionKey: 'shabbos',
  labelHe: 'שבת',
  labelEn: 'Shabbos',
  startDate: '2025-11-07',
  greetingHe: 'א גוט שבת',
  days: [
    {
      dayOffset: -1,
      dayLabelHe: 'ערב שבת',
      dayLabelEn: 'Erev Shabbos',
      date: '2025-11-07',
      hebrewDate: '16 Cheshvan 5786',
      hebrewDateHe: 'ט״ז חשון תשפ״ו',
      flags: ['Erev Shabbos'],
      flagKeys: ['erevShabbos'],
      parsha: 'וירא',
      namedDay: null,
      specialShabbos: [],
      mevorchim: null,
      anchors: { sunset: SHKIA, sunrise: '6:29 AM' },
      entries: [
        {
          ...EREV,
          key: 'mincha_erev_shabbos',
          labelHe: 'מנחה וקבלת שבת',
          labelEn: 'Mincha & Kabbalas Shabbos',
          time: '4:06 PM',
          basis: 'shkia − 40',
          formula: offsetOf('Shkia', 40, 'before', SHKIA),
        },
        {
          ...EREV,
          key: 'candle_lighting',
          labelHe: 'הדלקת נרות',
          labelEn: 'Candle lighting',
          time: '4:28 PM',
          basis: 'shkia − 18',
          formula: offsetOf('Shkia', 18, 'before', SHKIA),
        },
      ],
    },
    {
      dayOffset: 0,
      dayLabelHe: 'שבת קודש',
      dayLabelEn: 'Shabbos',
      date: '2025-11-08',
      hebrewDate: '17 Cheshvan 5786',
      hebrewDateHe: 'י״ז חשון תשפ״ו',
      flags: ['Shabbos'],
      flagKeys: ['dayIsShabbos'],
      parsha: 'וירא',
      namedDay: null,
      specialShabbos: [],
      mevorchim: null,
      anchors: { sunset: SHKIA, sunrise: '6:30 AM' },
      entries: [
        {
          ...DAY,
          key: 'shacharis_shabbos',
          labelHe: 'שחרית',
          labelEn: 'Shacharis',
          time: '8:45 AM',
          basis: 'set time',
          rootZman: null,
          formula: fixedAt('08:45'),
        },
        {
          ...DAY,
          key: 'mincha_shabbos',
          labelHe: 'מנחה',
          labelEn: 'Mincha',
          time: '4:06 PM',
          basis: 'shkia − 40',
          formula: offsetOf('Shkia', 40, 'before', SHKIA),
        },
        {
          ...DAY,
          key: 'maariv_motzash',
          labelHe: 'מעריב',
          labelEn: 'Maariv',
          time: '5:46 PM',
          basis: 'shkia + 60',
          formula: offsetOf('Shkia', 60, 'after', SHKIA),
        },
        {
          ...DAY,
          key: 'shabbos_ends',
          labelHe: 'מוצאי שבת',
          labelEn: 'Shabbos ends',
          time: '5:58 PM',
          basis: 'tzeis 72',
          rootZman: 'tzeis',
          formula: offsetOf('Shkia', 72, 'after', SHKIA),
        },
      ],
    },
  ],
}

export const DEMO_SCHEDULE: LuachResult = withPlacementIds(SAMPLE)
